/**
 * Copyright 2022-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { ExtendedTooltip, FlexContainer } from "@nordcloud/gnui";
import {
  EventActionScope,
  EventStatus,
  EventViewAction,
  IncomingEventsListBatchActionFragment,
} from "~/generated/graphql";
import { StyledSVGIcon } from "~/components/Styled/StyledSVGIcon";
import { isEmpty } from "~/tools";
import {
  Status,
  getEventActionBatchStatusWithAttention,
  getEventEntityStatus,
  getEventStatusWithAttention,
} from "../helpers";
import { EntityType, EventEntity } from "../types";

type ActionItem =
  | EventViewAction
  | EventEntity
  | IncomingEventsListBatchActionFragment;

type Props = {
  status?: string;
  actionLength?: number;
  type: EntityType;
  size?: "md" | "lg";
  statusDetails?: Status;
  actions?: ActionItem[];
};

export function TooltipIconStatus(props: Props) {
  const statusInfo = props.statusDetails ?? getStatusInfo(props);

  return (
    <FlexContainer justifyContent="center" alignItems="center">
      <ExtendedTooltip
        status={statusInfo.tooltipColor}
        caption={statusInfo.description}
        hideTimeout={0}
        position="start"
      >
        <FlexContainer justifyContent="center" alignItems="center">
          <StyledSVGIcon
            size={props.size}
            name={statusInfo.icon}
            color={statusInfo.color()}
            pathColors={statusInfo.pathColors}
          />
        </FlexContainer>
      </ExtendedTooltip>
    </FlexContainer>
  );
}

function getStatusInfo({
  status,
  actionLength,
  type,
  actions = [],
}: Omit<Props, "size" | "statusDetails">): Status {
  if (
    [EntityType.FUTURE_EVENT, EntityType.FUTURE_ACTION_BATCH].includes(type)
  ) {
    return getEventStatusWithAttention({
      status,
      actionsLength: actionLength,
      actions,
    });
  }

  if (type === EntityType.ACTION_BATCH) {
    return getEventActionBatchStatusWithAttention({ status, actions });
  }

  if (type === EntityType.ACTION) {
    return getEventEntityStatus(status);
  }

  const eventStatus = getEventStatusWithAttention({
    status,
    actionsLength: actionLength,
    actions,
  });

  if (status === EventStatus.Success && eventStatus.icon === "successFilled") {
    const icon = "statusSuccessDetailed";

    if (isEmpty(actions)) {
      return {
        ...eventStatus,
        icon,
        description: "No actions added.",
      };
    }

    if (hasNoResources(actions)) {
      return {
        ...eventStatus,
        icon,
        description: "No resources added.",
      };
    }
  }

  return eventStatus;
}

function hasNoResources(actions: ActionItem[]): boolean {
  return actions.every((action) => {
    switch (action.__typename) {
      case "EventViewAction":
        return (
          action.scope === EventActionScope.Resource &&
          (action?.resourcesFailed ?? 0) +
            (action?.resourcesSucceeded ?? 0) +
            (action?.resourcesNotCompleted ?? 0) ===
            0
        );

      case "EventAction":
        return (
          action.scope === EventActionScope.Resource &&
          isEmpty(action.resources ?? []) &&
          isEmpty(action.resourceGroups ?? [])
        );

      default:
        return false;
    }
  });
}
